import { Injectable, NgZone } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, skip, Subject, take } from 'rxjs';
import { scopeForTranslationKey } from '../transloco/translation-scope';
import { ZoomLevelService } from './zoom-level.service';
import { ModuleConfig, ModuleConfigService } from './module-config.service';
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root',
})
export class HeadlineService {
    headline: string[];
    /** The keys as the page set them, kept for a language switch. */
    private lastHeadline: (string | undefined)[] = [];
    idString = '';
    isMainPage: boolean;
    showCheckbox: boolean = true;

    enabled: boolean = true;
    showIframe: boolean;
    /** Current module is pinned to the old PHP page (no Angular version). */
    phpOnly: boolean = false;

    checkboxChanged = new Subject<void>();

    /**
     * Runtime state for each module's iframe visibility.
     * Initialized from module-config.json defaults at app startup.
     * Toggled at runtime when user clicks the "Alt" checkbox.
     */
    moduleStates: Record<string, boolean> = {};

    constructor(
        private zoomLevelService: ZoomLevelService,
        private moduleConfigService: ModuleConfigService,
        private translocoService: TranslocoService,
        private ngZone: NgZone
    ) {
        const config = this.moduleConfigService.config;
        for (const [moduleId, moduleConfig] of Object.entries(config)) {
            this.moduleStates[moduleId] = this.initialShowIframe(moduleConfig);
        }

        // A page sets its headline once, so the toolbar would keep showing the
        // previous language until the next navigation.
        this.translocoService.langChanges$
            .pipe(skip(1))
            .subscribe(() => void this.republishHeadline());
    }

    setHeadline(
        headline: (string | undefined)[],
        idString: string,
        isMainPage = true,
        showCheckbox = true
    ) {
        // Callers often reach this after an `await` (e.g. right after saving), which
        // resumes outside the Angular zone. A timeout scheduled there would apply the
        // new headline without triggering change detection, so the toolbar kept showing
        // the previous headline until the next user interaction. Re-enter the zone so
        // the update is always picked up.
        this.ngZone.run(() => {
            setTimeout(async () => {
                // The toolbar renders these keys through the transloco pipe,
                // which caches the raw key when the scope behind it is not in
                // the store yet. Load the scope the keys actually need before
                // publishing them — the layout used to pre-provide every scope
                // a page might set, which cost ~30 requests per cold load.
                await this.loadHeadlineScopes(headline);

                this.lastHeadline = headline;
                this.headline = headline.filter(
                    (h): h is string => h !== undefined
                );
                this.idString = idString;
                this.isMainPage = isMainPage;
                this.showCheckbox = showCheckbox;

                const config = this.moduleConfigService.getConfig(idString);
                this.phpOnly = config?.phpOnly ?? false;
                // A PHP-only module has no Angular page to switch to, so the
                // toggle stays hidden even in the test system.
                this.enabled = this.phpOnly
                    ? false
                    : (config?.enableToggle ?? false);
                const defaultShowIframe = config
                    ? this.initialShowIframe(config)
                    : false;
                this.showIframe = this.phpOnly
                    ? true
                    : (this.moduleStates[idString] ?? defaultShowIframe);
            }, 1);
        });
    }

    /**
     * Which view a module opens on before the user touches the "Alt" toggle.
     *
     * The test system starts every module on its Angular page so the migrated
     * pages are what gets tested — except for the modules that are staying on
     * PHP, which look the same as in the live system (HIP-76).
     */
    private initialShowIframe(config: ModuleConfig): boolean {
        if (config.phpOnly) {
            return true;
        }
        return environment.isTestSystem ? false : config.showIframe;
    }

    /**
     * Re-renders the current headline in the new language.
     *
     * The transloco pipe keeps the value it once resolved for a key, so
     * re-assigning the same strings would not update anything — the entries
     * have to disappear first, which drops the views and their pipes.
     */
    private async republishHeadline(): Promise<void> {
        const headline = this.lastHeadline;
        if (headline.length === 0) {
            return;
        }
        this.headline = [];
        await this.loadHeadlineScopes(headline);
        this.ngZone.run(() => {
            this.headline = headline.filter(
                (h): h is string => h !== undefined
            );
        });
    }

    /**
     * Loads the scopes the headline keys belong to. Scopes that are already
     * in the store resolve without a request, so a repeat visit costs nothing.
     */
    private async loadHeadlineScopes(
        headline: (string | undefined)[]
    ): Promise<void> {
        const scopes = new Set(
            headline
                .filter((item): item is string => !!item)
                .map(item => scopeForTranslationKey(item))
                .filter((scope): scope is string => scope !== null)
        );
        if (scopes.size === 0) {
            return;
        }

        try {
            await Promise.all(
                [...scopes].map(scope =>
                    firstValueFrom(
                        this.translocoService
                            .selectTranslation(scope)
                            .pipe(take(1))
                    )
                )
            );
        } catch {
            // A headline that falls back to its raw key is better than a
            // toolbar that never updates.
        }
    }

    toggleOldSystem() {
        if (this.showIframe) {
            this.zoomLevelService.changeZoom();
        }
        this.moduleStates[this.idString] = this.showIframe;
        this.checkboxChanged.next();
    }
}
